import dayjs from 'dayjs'
import { useMemo } from 'react'

interface Props<T> {
  items: T[]
  itemDateKey: string
  itemDateGetter?: (item: T) => string | undefined
  itemDateFormat?: string
  dateFormat?: string
}

interface BuildDateBadgedListProps<T> extends Props<T> {}

interface IDateBadge {
  id: string
  label: string
}

interface BadgeElement {
  type: 'badge'
  item: IDateBadge
}

interface OriginalElement<T> {
  type: 'item'
  item: T
  extra: {
    isFirstOfType: boolean
    isLastOfType: boolean
  }
}

type BadgesAndElements<T> = (BadgeElement | OriginalElement<T>)[]

const _findByKey = (json: Record<string, any>, keys: string[]) => {
  if (!json) return undefined
  const result = json[keys.shift()]
  if (keys.length === 0) return result
  return _findByKey(result, keys)
}

export const useDateBadgedList = <T>({
  items,
  itemDateKey,
  itemDateGetter,
  itemDateFormat,
  dateFormat = 'MMMM YYYY'
}: Props<T>): BadgesAndElements<T> =>
  useMemo(
    () =>
      buildDateBadgedList({
        items,
        itemDateKey,
        itemDateGetter,
        itemDateFormat,
        dateFormat
      }),
    [items, itemDateKey, itemDateGetter, itemDateFormat, dateFormat]
  )

export const buildDateBadgedList = <T>({
  items,
  itemDateKey,
  itemDateGetter,
  itemDateFormat,
  dateFormat = 'MMMM YYYY'
}: BuildDateBadgedListProps<T>): BadgesAndElements<T> => {
  const itemsAndBadges: BadgesAndElements<T> = []
  const resolveDate = (item: T | undefined) => {
    if (!item) {
      return undefined
    }
    if (itemDateGetter) {
      return itemDateGetter(item)
    }
    return _findByKey(item as Record<string, any>, itemDateKey.split('.'))
  }

  items.forEach((thisItem, index) => {
    const lastItem = items[index - 1]
    const nextItem = items[index + 1]

    const lastDateString = resolveDate(lastItem)
    const thisDateString = resolveDate(thisItem)
    const nextDateString = resolveDate(nextItem)

    const thisDate = dayjs(thisDateString, itemDateFormat).format(dateFormat)
    const lastDate = lastItem && dayjs(lastDateString, itemDateFormat).format(dateFormat)
    const nextDate = nextItem && dayjs(nextDateString, itemDateFormat).format(dateFormat)

    const isFirstOfType = thisDate !== lastDate
    const isLastOfType = thisDate !== nextDate

    if (isFirstOfType) {
      itemsAndBadges.push({
        type: 'badge',
        item: {
          id: `badge-${thisDate}-${index}`,
          label: thisDate
        }
      })
    }

    itemsAndBadges.push({
      type: 'item',
      item: thisItem,
      extra: {
        isFirstOfType,
        isLastOfType
      }
    })
  })

  return itemsAndBadges
}
